import React from "react";
import styled from "styled-components";
import { Moon, Sun } from "lucide-react";
import skyoneTokens from "../../theme/skyoneTokens.json";

const {
  FA_PURPLE,
  FA_PURPLE_DEEP,
  FA_LIME,
  FA_LIME_HOVER,
  FA_LIME_LIGHT,
  FA_LIME_LIGHT_HOVER,
  FA_PURPLE_RGB,
  FA_LIME_RGB,
  FA_LIME_LIGHT_RGB,
  FA_PAGE,
  FA_PAGE_DARK,
  FA_BRAND_PANEL_DARK,
  FA_RING_GRAY,
} = skyoneTokens;

export const PageContainer = styled.div`
  display: flex;
  min-height: 100vh;
  width: 100%;
  background-color: ${props => props.darkMode ? FA_PAGE_DARK : FA_PAGE};
  transition: background-color 0.5s ease;
  position: relative;
`;

export const BrandingSection = styled.div`
  width: 25%;
  background: ${props => props.darkMode ? FA_BRAND_PANEL_DARK : FA_PAGE};
  display: flex;
  flex-direction: column;
  padding: 2rem;
  position: relative;
  overflow-y: auto;
  transition: background-color 0.5s ease, color 0.5s ease;
  border-right: ${props =>
    props.darkMode
      ? `1px solid rgba(${FA_LIME_RGB}, 0.12)`
      : `1px solid rgba(${FA_PURPLE_RGB}, 0.12)`};
  
  h1, h2, p, .text {
    color: ${props => props.darkMode ? "#e2e8f0" : "#0a0a0a"};
  }
`;

export const LogoContainer = styled.div`
  width: 100%;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    display: block;
    width: auto;
    max-width: 100%;
    max-height: 112px;
    height: auto;
    object-fit: contain;
    object-position: center center;
    margin-left: auto;
    margin-right: auto;
  }
`;

export const BrandingContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  h1 {
    font-size: 1.75rem;
    font-weight: bold;
    color: ${props => props.darkMode ? "#e2e8f0" : FA_PURPLE_DEEP};
    margin-bottom: 0.75rem;
    position: relative;
    text-align: center;
    transition: color 0.5s ease;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 50%;
      transform: translateX(-50%);
      width: 72px;
      height: 3px;
      background: linear-gradient(
        90deg,
        transparent 0%,
        ${props => (props.darkMode ? FA_LIME : FA_PURPLE)} 35%,
        ${props => (props.darkMode ? FA_PURPLE : FA_LIME_LIGHT)} 65%,
        transparent 100%
      );
      border-radius: 2px;
    }
  }
  p {
    font-size: 1rem;
    color: ${props => props.darkMode ? "#94a3b8" : "#52525b"};
    margin-bottom: 2rem;
    text-align: center;
    transition: color 0.5s ease;
  }
`;

export const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  text-align: left;
  width: 100%;
`;

export const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  .icon {
    width: 2.5rem;
    height: 2.5rem;
    background: ${props =>
      props.darkMode ? `rgba(${FA_LIME_RGB}, 0.1)` : `rgba(${FA_PURPLE_RGB}, 0.1)`};
    border-radius: calc(0.625rem);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background-color 0.5s ease;
  }
  .text {
    .title {
      font-weight: 600;
      color: ${props => props.darkMode ? "#e2e8f0" : "#18181b"};
      margin-bottom: 0.25rem;
      font-size: 0.95rem;
      transition: color 0.5s ease;
    }
    .description {
      font-size: 0.8rem;
      color: ${props => props.darkMode ? "#94a3b8" : "#71717a"};
      transition: color 0.5s ease;
    }
  }
`;

export const FormSection = styled.div`
  width: 75%;
  background: ${props =>
    props.darkMode
      ? `linear-gradient(145deg, #0a0610 0%, #1a0f24 45%, #0c0812 100%),
         radial-gradient(circle at 18% 28%, rgba(${FA_LIME_RGB}, 0.055) 0%, transparent 45%)`
      : `linear-gradient(145deg, #120a18 0%, #1f1230 48%, #0e0814 100%),
         radial-gradient(circle at 18% 28%, rgba(${FA_PURPLE_RGB}, 0.09) 0%, transparent 45%)`};
  background-attachment: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  overflow: hidden;
  transition: background-color 0.5s ease;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props =>
      props.darkMode
        ? `radial-gradient(circle at 82% 55%, rgba(${FA_PURPLE_RGB}, 0.1) 0%, transparent 52%)`
        : `radial-gradient(circle at 82% 55%, rgba(${FA_LIME_LIGHT_RGB}, 0.055) 0%, transparent 52%)`};
    pointer-events: none;
  }
`;

// Restored original slider container structure
export const FormCard = styled.div`
  background-color: ${props => (props.darkMode ? "#0f0a14" : "#fafafa")};
  border-radius: calc(0.625rem + 2px);
  box-shadow: ${props =>
    props.darkMode
      ? `0 24px 48px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(${FA_LIME_RGB}, 0.1),
         0 0 40px rgba(${FA_PURPLE_RGB}, 0.09)`
      : `0 24px 48px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(${FA_PURPLE_RGB}, 0.08),
         0 0 36px rgba(${FA_LIME_LIGHT_RGB}, 0.08)`};
  position: relative;
  overflow: hidden;
  width: 678px;
  max-width: 100%;
  min-height: 500px;
  padding: 2rem 0;
  transition: all 0.5s ease;
  border: ${props =>
    props.darkMode
      ? `1px solid rgba(${FA_LIME_RGB}, 0.12)`
      : `1px solid rgba(${FA_PURPLE_RGB}, 0.1)`};
`;

export const SignUpContainer = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  transition: all 0.6s ease-in-out;
  left: 0;
  width: 50%;
  opacity: 0;
  z-index: ${props => props.signingIn ? 1 : 6};
  ${props =>
    props.signingIn !== true
      ? `
  transform: translateX(100%);
  opacity: 1;
  `
      : null}
`;

export const SignInContainer = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  transition: all 0.6s ease-in-out;
  left: 0;
  width: 50%;
  z-index: ${props => props.signingIn ? 5 : 1};
  ${props => (props.signingIn !== true ? `transform: translateX(100%);` : null)}
`;

export const StyledForm = styled.form`
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 0 50px;
  height: 100%;
  text-align: center;
`;

export const StyledTitle = styled.h1`
  font-weight: bold;
  margin: 0 0 1rem 0;
  color: ${props => props.darkMode ? "#fafafa" : FA_PURPLE_DEEP};
  transition: color 0.5s ease;
`;

export const StyledInput = styled.input`
  background-color: ${props => props.darkMode ? "rgba(255,255,255,0.06)" : "#f4f4f5"};
  border: ${props =>
    props.darkMode ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(24, 24, 27, 0.08)"};
  border-radius: calc(0.625rem + 4px);
  padding: 12px 15px;
  margin: 8px 0;
  width: 100%;
  color: ${props => props.darkMode ? "#fafafa" : "#18181b"};
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${props =>
      props.darkMode ? FA_RING_GRAY : FA_PURPLE};
    box-shadow: ${props =>
      props.darkMode
        ? `0 0 0 2px rgba(100, 116, 139, 0.35)`
        : `0 0 0 2px rgba(${FA_PURPLE_RGB}, 0.22)`};
  }
  
  &::placeholder {
    color: ${props => props.darkMode ? "#94a3b8" : "#71717a"};
  }
`;

export const StyledButton = styled.button`
  border-radius: 999px;
  border: 1px solid
    ${props =>
      props.$darkMode === false
        ? `rgba(${FA_LIME_LIGHT_RGB}, 0.38)`
        : `rgba(${FA_LIME_RGB}, 0.38)`};
  background: ${props =>
    props.$darkMode === false
      ? `linear-gradient(135deg, ${FA_LIME_LIGHT} 0%, ${FA_LIME_LIGHT_HOVER} 100%)`
      : `linear-gradient(135deg, ${FA_LIME} 0%, ${FA_LIME_HOVER} 100%)`};
  color: ${FA_PURPLE_DEEP};
  font-size: 12px;
  font-weight: bold;
  padding: 12px 45px;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25),
    ${props =>
      props.$darkMode === false
        ? `0 0 20px rgba(${FA_LIME_LIGHT_RGB}, 0.16)`
        : `0 0 20px rgba(${FA_LIME_RGB}, 0.14)`};
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.35);
    transform: translate(-50%, -50%);
    transition: width 0.6s, height 0.6s;
  }

  &:hover {
    background: ${props =>
      props.$darkMode === false
        ? `linear-gradient(135deg, ${FA_LIME_LIGHT_HOVER} 0%, ${FA_LIME_LIGHT} 100%)`
        : `linear-gradient(135deg, ${FA_LIME_HOVER} 0%, ${FA_LIME} 100%)`};
    border-color: ${props =>
      props.$darkMode === false ? FA_LIME_LIGHT : FA_LIME};
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.3),
      ${props =>
        props.$darkMode === false
          ? `0 0 28px rgba(${FA_LIME_LIGHT_RGB}, 0.24)`
          : `0 0 28px rgba(${FA_LIME_RGB}, 0.22)`};
    
    &::before {
      width: 300px;
      height: 300px;
    }
  }

  &:active {
    transform: translateY(0) scale(0.98);
    box-shadow: ${props =>
      props.$darkMode === false
        ? `0 4px 14px rgba(${FA_LIME_LIGHT_RGB}, 0.2)`
        : `0 4px 14px rgba(${FA_LIME_RGB}, 0.18)`};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(${FA_PURPLE_RGB}, 0.28);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
    
    &:hover {
      transform: none;
      box-shadow: none;
      background: ${props =>
        props.$darkMode === false
          ? `linear-gradient(135deg, ${FA_LIME_LIGHT} 0%, ${FA_LIME_LIGHT_HOVER} 100%)`
          : `linear-gradient(135deg, ${FA_LIME} 0%, ${FA_LIME_HOVER} 100%)`};
    }
  }
`;

/** Same lime fill + purple label as StyledButton (Greetings / Welcome overlay actions) */
export const GhostButton = styled(StyledButton)``;

export const StyledAnchor = styled.a`
  color: #333;
  font-size: 14px;
  text-decoration: none;
  margin: 15px 0;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

export const OverlayContainer = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  width: 50%;
  height: 100%;
  overflow: hidden;
  transition: transform 0.6s ease-in-out;
  z-index: 100;
  ${props =>
    props.signingIn !== true ? `transform: translateX(-100%);` : null}
`;

export const Overlay = styled.div`
  background: linear-gradient(
    125deg,
    ${FA_PURPLE_DEEP} 0%,
    ${FA_PURPLE} 42%,
    #4a2d82 100%
  );
  background-repeat: no-repeat;
  background-size: cover;
  background-position: 0 0;
  color: #fafafa;
  position: relative;
  left: -100%;
  height: 100%;
  width: 200%;
  transform: translateX(0);
  transition: transform 0.6s ease-in-out;
  box-shadow: inset 0 0 60px rgba(${FA_LIME_RGB}, 0.06);
  ${props => (props.signingIn !== true ? `transform: translateX(50%);` : null)}
`;

export const OverlayPanel = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 0 40px;
  text-align: center;
  top: 0;
  height: 100%;
  width: 50%;
  transform: translateX(0);
  transition: transform 0.6s ease-in-out;
`;

export const LeftOverlayPanel = styled(OverlayPanel)`
  transform: translateX(-20%);
  ${props => (props.signingIn !== true ? `transform: translateX(0);` : null)}
`;

export const RightOverlayPanel = styled(OverlayPanel)`
  right: 0;
  transform: translateX(0);
  ${props => (props.signingIn !== true ? `transform: translateX(20%);` : null)}
`;

export const StyledParagraph = styled.p`
  font-size: 16px;
  font-weight: 200;
  line-height: 24px;
  letter-spacing: 0.5px;
  margin: 20px 0 30px;
  opacity: 0.95;
`;

export const ErrorMessage = styled.p`
  color: #ef4444;
  font-size: 12px;
  margin: 5px 0;
  text-align: left;
  width: 100%;
  transition: color 0.3s ease;
`;

export const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export const OverlayTitle = styled.h1`
  font-weight: bold;
  margin: 0;
  color: #fff;
`;

const ThemeToggleBtn = styled.button`
  position: fixed;
  top: 2rem;
  right: 2rem;
  z-index: 1000;
  width: 60px;
  height: 30px;
  border-radius: 15px;
  border: 1px solid ${props =>
    props.darkMode ? `rgba(${FA_LIME_RGB}, 0.2)` : `rgba(${FA_PURPLE_RGB}, 0.16)`};
  background-color: ${props => (props.darkMode ? "#1a1424" : "#f4f4f5")};
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 3px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
  }

  &:active {
    transform: scale(0.95);
  }

  &::before {
    content: "";
    position: absolute;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${props => (props.darkMode ? FA_LIME : FA_PURPLE)};
    left: ${props => (props.darkMode ? "33px" : "3px")};
    top: 3px;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    z-index: 1;
  }
`;

const ThemeToggleIconSlot = styled.span`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 0;
  color: #facc15;
  transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const ThemeToggleMoonWrap = styled(ThemeToggleIconSlot)`
  left: 36px;
  opacity: ${props => (props.$darkMode ? 0 : 1)};
`;

const ThemeToggleSunWrap = styled(ThemeToggleIconSlot)`
  left: 8px;
  opacity: ${props => (props.$darkMode ? 1 : 0)};
`;

export function ThemeToggle({ onClick, darkMode, ...rest }) {
  return (
    <ThemeToggleBtn
      type="button"
      onClick={onClick}
      darkMode={darkMode}
      aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
      {...rest}
    >
      <ThemeToggleMoonWrap $darkMode={darkMode}>
        <Moon size={14} strokeWidth={2.25} aria-hidden />
      </ThemeToggleMoonWrap>
      <ThemeToggleSunWrap $darkMode={darkMode}>
        <Sun size={14} strokeWidth={2.25} aria-hidden />
      </ThemeToggleSunWrap>
    </ThemeToggleBtn>
  );
}